import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getAssociations, getBuilders, generateOutreach, pitchInstaller, sendOne, sendBatch, getBatch, stopBatch, getSendStatus } from "../lib/api";
import { useCountry } from "../lib/countryContext";
import { useSenderProfile } from "../lib/senderProfile";
import { SectionTitle, PrimaryButton, SecondaryButton, EmptyState } from "../components/UIKit";
import { SenderProfileCard } from "../components/SenderProfileCard";
import { SalesPlaybook } from "../components/SalesPlaybook";
import { AutopilotCard } from "../components/AutopilotCard";
import { CopySimple, Sparkle, Envelope, PaperPlaneTilt, Buildings, Factory, Rocket, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

function personalize(text, profile) {
  if (!text) return text;
  const signature = [profile.name, profile.company].filter(Boolean).join("\n");
  return signature ? text.replace(/\[Your Name\]/g, signature) : text;
}

function extractSubject(text) {
  if (!text) return "";
  const m = text.match(/^\s*(?:Betreff|Subject|Objet|Asunto|Oggetto|Onderwerp|Emne|Ämne|Aihe|Θέμα|Temat|Assunto|Předmět|Tárgy|Temă)\s*:\s*(.+)$/mi);
  return m ? m[1].trim() : "";
}

function extractBody(text) {
  if (!text) return "";
  return text.replace(/^\s*(?:Betreff|Subject|Objet|Asunto|Oggetto|Onderwerp|Emne|Ämne|Aihe|Θέμα|Temat|Assunto|Předmět|Tárgy|Temă)\s*:.+\n+/mi, "").trim();
}

const MODES = [
  {
    id: "sell-installers",
    label: "SELL INSTALLER LIST → BUYERS",
    icon: Buildings,
    hint: "Pitch trade associations, distributors, financing companies with your installer contact list.",
  },
  {
    id: "sell-manufacturers",
    label: "SELL MANUFACTURER LIST → INSTALLERS",
    icon: Factory,
    hint: "Pitch installers with your curated manufacturer list (better margins, cut out middlemen).",
  },
];

export default function OutreachPage() {
  const { selected, countries } = useCountry();
  const location = useLocation();
  const [profile] = useSenderProfile();
  const [mode, setMode] = useState(location.state?.mode || "sell-installers");
  const [model, setModel] = useState(() => localStorage.getItem("emailModel") || "claude");
  const [tone, setTone] = useState(() => localStorage.getItem("emailTone") || "professional");
  const [targets, setTargets] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(location.state?.associationId || null);
  const [email, setEmail] = useState("");
  const [preview, setPreview] = useState("");
  const [showPreview, setShowPreview] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [lang, setLang] = useState("");
  const [sendStatus, setSendStatus] = useState(null);
  const [activeBatch, setActiveBatch] = useState(null);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [batchCount, setBatchCount] = useState(10);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const s = await getSendStatus();
        if (alive) {
          setSendStatus(s);
          setActiveBatch(s.active_batch);
        }
      } catch (e) {}
    };
    tick();
    const iv = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const country = countries.find((c) => c.code === selected);

  const setModelPersist = (m) => { localStorage.setItem("emailModel", m); setModel(m); };
  const setTonePersist = (t) => { localStorage.setItem("emailTone", t); setTone(t); };

  useEffect(() => {
    setEmail("");
    setSelectedTarget(null);
    const load = mode === "sell-installers"
      ? getAssociations({ country_code: selected })
      : getBuilders({ country_code: selected });
    load.then((data) => {
      setTargets(data);
      if (data.length) setSelectedTarget(data[0].id);
    });
  }, [selected, mode]);

  const generate = async () => {
    if (!selectedTarget) return;
    setGenerating(true);
    setEmail("");
    try {
      const opts = { model, tone, sender: profile };
      const r = mode === "sell-installers"
        ? await generateOutreach(selectedTarget, opts)
        : await pitchInstaller(selectedTarget, opts);
      setEmail(personalize(r.email, profile));
      setPreview(personalize(r.preview || "", profile));
      setLang(r.language);
      toast.success(`Drafted in ${r.language} · ${model} · ${tone}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    }
    setGenerating(false);
  };

  const copy = async () => {
    await navigator.clipboard.writeText(email);
    toast.success("Copied to clipboard");
  };

  const currentTarget = targets.find((t) => t.id === selectedTarget);

  const openMailApp = () => {
    if (!email) return;
    const to = currentTarget?.email || "";
    const defaultSubject = mode === "sell-installers"
      ? `Solar installer contacts for ${currentTarget?.name || country?.name}`
      : `EU solar manufacturer contacts — better margins for ${currentTarget?.name || "your installs"}`;
    const subject = extractSubject(email) || defaultSubject;
    const body = extractBody(email);
    const url = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
  };

  const sendNow = async () => {
    if (!email || !currentTarget?.email) {
      toast.error("Recipient has no email on file — use OPEN IN MAIL APP instead.");
      return;
    }
    setSending(true);
    try {
      const subject = extractSubject(email) || `Message from ${sendStatus?.sender_name || "SolarBridge EU"}`;
      const body = extractBody(email);
      const res = await sendOne({
        target_id: currentTarget.id,
        kind: isInstaller ? "installer" : "buyer",
        subject,
        body,
        language: lang || country?.lang || "English",
      });
      toast.success(`Sent to ${currentTarget.name} · ${res.sent_today}/${res.daily_cap} today`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
    setSending(false);
  };

  const startBatch = () => {
    const max = sendStatus?.remaining_today ?? 20;
    setBatchCount(Math.min(10, max));
    setBatchModalOpen(true);
  };

  const confirmBatch = async () => {
    const n = parseInt(batchCount, 10);
    if (isNaN(n) || n < 1) { toast.error("Enter a positive number"); return; }
    setBatchModalOpen(false);
    try {
      const res = await sendBatch({
        country_code: selected,
        mode,
        limit: n,
        model,
        tone,
      });
      toast.success(`Batch queued: ${res.queued} emails to ${country?.name}. Runs in background.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch failed to start");
    }
  };

  const cancelBatch = async () => {
    if (!activeBatch) return;
    try {
      await stopBatch(activeBatch.id);
      toast.info("Batch stopped");
    } catch (e) {}
  };

  const isInstaller = mode === "sell-manufacturers";
  const modeMeta = MODES.find((m) => m.id === mode);

  return (
    <div className="px-4 sm:px-6 py-8 max-w-7xl">
      <SectionTitle overline={`${country?.flag || ""} OUTREACH DRAFTS`} title="Pitch Cockpit">
        {sendStatus?.configured && (
          <button
            data-testid="batch-send-btn"
            onClick={startBatch}
            disabled={sendStatus.remaining_today <= 0 || !!activeBatch}
            className="bg-[#0A0A0A] text-[#D4FF00] px-4 py-2.5 text-xs tracking-[0.2em] uppercase shadow-[2px_2px_0px_#0047FF] hover:bg-[#111] active:translate-y-[2px] active:translate-x-[2px] active:shadow-none disabled:bg-[#525252] disabled:shadow-none disabled:cursor-not-allowed transition-colors duration-150 inline-flex items-center gap-2"
            style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
          >
            <Rocket size={14} weight="bold" /> BATCH SEND
          </button>
        )}
      </SectionTitle>

      <AutopilotCard />

      {/* Batch modal */}
      {batchModalOpen && (
        <div
          data-testid="batch-modal-overlay"
          onClick={() => setBatchModalOpen(false)}
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        >
          <div
            data-testid="batch-modal"
            onClick={(e) => e.stopPropagation()}
            className="bg-white border-2 border-black shadow-[6px_6px_0px_#0047FF] max-w-md w-full p-6"
          >
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#0047FF] mb-1" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
              BATCH SEND · {country?.flag} {country?.name}
            </div>
            <h2 className="text-2xl tracking-tight mb-4" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}>
              How many emails?
            </h2>
            <div className="text-sm text-[#525252] mb-4">
              Sending to {isInstaller ? "installers" : "buyers"} in {country?.name}. The bot generates a unique pitch per recipient in {country?.lang}, then sends via {sendStatus?.sender_email}. Runs in background — you can close this tab.
            </div>
            <div className="mb-4">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#525252] mb-1" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                Number of emails
              </div>
              <input
                data-testid="batch-count-input"
                type="number"
                min={1}
                max={sendStatus?.remaining_today ?? 20}
                value={batchCount}
                onChange={(e) => setBatchCount(e.target.value)}
                className="w-full border-2 border-black px-3 py-2 text-2xl tabular-nums outline-none focus:bg-[#F4F4F2]"
                style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700 }}
              />
              <div className="text-xs text-[#525252] mt-2" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                Daily cap: {sendStatus?.remaining_today ?? "?"} remaining today
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                data-testid="batch-modal-cancel"
                onClick={() => setBatchModalOpen(false)}
                className="border border-black px-4 py-2 text-xs tracking-[0.2em] uppercase hover:bg-black/5"
                style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
              >
                CANCEL
              </button>
              <button
                data-testid="batch-modal-confirm"
                onClick={confirmBatch}
                className="bg-[#0A0A0A] text-[#D4FF00] px-4 py-2 text-xs tracking-[0.2em] uppercase shadow-[2px_2px_0px_#0047FF] hover:bg-[#111] active:translate-y-[2px] active:translate-x-[2px] active:shadow-none inline-flex items-center gap-2"
                style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
              >
                <Rocket size={12} weight="bold" /> START BATCH
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send status bar */}
      {sendStatus?.configured && (
        <div className="border border-black bg-white p-3 mb-6 flex flex-wrap items-center justify-between gap-3" data-testid="send-status">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <div className="text-xs" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
              Sending from <b>{sendStatus.sender_name} &lt;{sendStatus.sender_email}&gt;</b>
            </div>
          </div>
          <div className="text-xs tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            <span className="text-[#525252]">Today:</span> <b>{sendStatus.sent_today}</b> / {sendStatus.daily_cap} · <span className="text-[#525252]">Remaining:</span> <b className="text-[#0047FF]">{sendStatus.remaining_today}</b>
          </div>
        </div>
      )}

      {/* Active batch progress */}
      {activeBatch && (
        <div className="border border-black bg-[#0A0A0A] text-[#D4FF00] p-3 mb-6" data-testid="batch-progress">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
              <CircleNotch size={12} weight="bold" className="animate-spin" />
              BATCH SENDING · {activeBatch.country_flag} {activeBatch.country_name} · {activeBatch.sent}/{activeBatch.limit}
            </div>
            <button onClick={cancelBatch} className="text-[10px] tracking-[0.2em] uppercase text-[#D4FF00]/70 hover:text-[#D4FF00]">STOP</button>
          </div>
          <div className="text-[10px] max-h-24 overflow-y-auto" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            {(activeBatch.log || []).slice(-6).map((l, i) => (
              <div key={i} className="opacity-80">&gt; {l.msg}</div>
            ))}
          </div>
        </div>
      )}

      {/* Mode toggle */}
      <div className="border border-black bg-white mb-6" data-testid="mode-toggle">
        <div className="grid grid-cols-1 sm:grid-cols-2">
          {MODES.map((m) => {
            const Icon = m.icon;
            const active = mode === m.id;
            return (
              <button
                key={m.id}
                data-testid={`mode-${m.id}`}
                onClick={() => setMode(m.id)}
                className={`p-4 text-left transition-colors duration-150 border-b sm:border-b-0 sm:border-r border-black/10 last:border-r-0 last:border-b-0 ${
                  active ? "bg-[#0A0A0A] text-white" : "hover:bg-black/[0.03]"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-6 h-6 flex items-center justify-center ${active ? "bg-[#D4FF00] text-[#0A0A0A]" : "bg-[#0A0A0A] text-[#D4FF00]"}`}>
                    <Icon size={12} weight="bold" />
                  </div>
                  <div className="text-[10px] tracking-[0.2em] uppercase" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                    {m.label}
                  </div>
                </div>
                <div className="text-xs opacity-80 pl-8">{m.hint}</div>
              </button>
            );
          })}
        </div>
      </div>

      <SenderProfileCard />

      {/* Model + Tone picker */}
      <div className="border border-black bg-white mb-6 grid grid-cols-1 md:grid-cols-2" data-testid="model-tone-picker">
        <div className="p-3 border-b md:border-b-0 md:border-r border-black/10">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#525252] mb-2" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
            AI MODEL
          </div>
          <div className="flex gap-1 flex-wrap">
            {[
              { id: "claude", label: "Claude Sonnet" },
              { id: "gemini", label: "Gemini 3 Flash" },
              { id: "gpt", label: "GPT-5.4" },
            ].map((m) => (
              <button
                key={m.id}
                data-testid={`model-${m.id}`}
                onClick={() => setModelPersist(m.id)}
                className={`px-3 py-1.5 text-[10px] tracking-[0.15em] uppercase border transition-colors duration-150 ${
                  model === m.id ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-black/25 hover:bg-black/5"
                }`}
                style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
        <div className="p-3">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#525252] mb-2" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
            TONE
          </div>
          <div className="flex gap-1 flex-wrap">
            {[
              { id: "professional", label: "Professional" },
              { id: "friendly", label: "Friendly" },
              { id: "direct", label: "Direct" },
              { id: "curious", label: "Curious" },
            ].map((t) => (
              <button
                key={t.id}
                data-testid={`tone-${t.id}`}
                onClick={() => setTonePersist(t.id)}
                className={`px-3 py-1.5 text-[10px] tracking-[0.15em] uppercase border transition-colors duration-150 ${
                  tone === t.id ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-black/25 hover:bg-black/5"
                }`}
                style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <SalesPlaybook />

      {targets.length === 0 ? (
        <EmptyState
          icon={<Envelope size={22} weight="bold" />}
          title={isInstaller
            ? `No installers yet in ${country?.name || "this country"}`
            : `No buyers yet in ${country?.name || "this country"}`}
          hint={isInstaller
            ? "Go to Installers and run a scan first. Also run a Manufacturer scan for this country so there's something to sell."
            : "Go to Buyers and run a scan first."}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[320px,1fr] gap-6">
          {/* Target list */}
          <div className="border border-black bg-white" data-testid="outreach-target-list">
            <div className="text-[10px] tracking-[0.25em] uppercase text-[#525252] p-3 border-b border-black/10" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
              {isInstaller ? "SELECT INSTALLER" : "SELECT BUYER"} · {targets.length}
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              {targets.map((t) => (
                <button
                  key={t.id}
                  data-testid={`target-item-${t.id}`}
                  onClick={() => { setSelectedTarget(t.id); setEmail(""); }}
                  className={`w-full text-left p-3 border-b border-black/10 last:border-b-0 transition-colors duration-150 ${
                    selectedTarget === t.id ? "bg-[#0A0A0A] text-white" : "hover:bg-black/[0.03]"
                  }`}
                >
                  <div className="text-[9px] tracking-[0.2em] uppercase opacity-70 mb-1" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                    {isInstaller ? (t.specialty || "installer") : (t.type || "other").replace("_", " ")}
                  </div>
                  <div className="text-sm leading-tight" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                    {t.name}
                  </div>
                  {t.city && (
                    <div className="text-[10px] opacity-70 mt-1" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                      {t.city}
                    </div>
                  )}
                  {t.email && (
                    <div className="text-[10px] opacity-70 mt-0.5" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                      ✉ {t.email}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Composer */}
          <div>
            {currentTarget && (
              <div className="border border-black bg-white p-4 mb-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-[#0047FF] mb-1" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                      TARGET · {isInstaller ? (currentTarget.specialty || "installer") : (currentTarget.type || "other").replace("_", " ")}
                    </div>
                    <div className="text-xl tracking-tight" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 800 }}>
                      {currentTarget.name}
                    </div>
                    <div className="text-sm text-[#525252] mt-1">
                      {isInstaller ? (currentTarget.notes || currentTarget.city) : currentTarget.why_buy}
                    </div>
                    {!currentTarget.email && (
                      <div className="mt-2 text-xs bg-[#FFF3B0] border border-black/15 px-2 py-1 inline-block" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                        ⚠ No email on file — you'll need to find their contact page manually
                      </div>
                    )}
                  </div>
                  <PrimaryButton testId="generate-email-btn" onClick={generate} disabled={generating}>
                    <Sparkle size={14} weight="bold" className="inline mr-1.5" />
                    {generating ? "GENERATING…" : "GENERATE EMAIL"}
                  </PrimaryButton>
                </div>
              </div>
            )}

            <div className="border border-black bg-white">
              <div className="flex items-center justify-between border-b border-black/10 px-4 py-2 gap-2 flex-wrap">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#525252]" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                  DRAFT {lang && `· ${lang.toUpperCase()}`}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {email && (
                    <>
                      <SecondaryButton testId="copy-email-btn" onClick={copy} className="py-1 px-2">
                        <CopySimple size={12} weight="bold" /> COPY
                      </SecondaryButton>
                      <button
                        data-testid="send-mailto-btn"
                        onClick={openMailApp}
                        className="bg-white border border-black text-black px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase hover:bg-black/5 transition-colors duration-150 inline-flex items-center gap-1.5"
                        style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
                      >
                        <PaperPlaneTilt size={12} weight="bold" /> MAIL APP
                      </button>
                      {sendStatus?.configured && currentTarget?.email && (
                        <button
                          data-testid="send-now-btn"
                          onClick={sendNow}
                          disabled={sending || sendStatus.remaining_today <= 0}
                          className="bg-[#0047FF] text-white px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase shadow-[2px_2px_0px_#0A0A0A] hover:bg-[#0038CC] active:translate-y-[2px] active:translate-x-[2px] active:shadow-none disabled:bg-[#525252] disabled:shadow-none transition-colors duration-150 inline-flex items-center gap-1.5"
                          style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}
                        >
                          <Rocket size={12} weight="bold" /> {sending ? "SENDING…" : "SEND NOW"}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
              <textarea
                data-testid="email-textarea"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={generating
                  ? "Drafting in the recipient's native language..."
                  : isInstaller
                    ? "Click GENERATE EMAIL to draft a pitch offering this installer your curated EU manufacturer list."
                    : "Click GENERATE EMAIL to draft outreach in the buyer's native language."}
                className="w-full min-h-[380px] p-4 text-sm bg-white outline-none resize-y"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              />
              {preview && (
                <div className="border-t border-black bg-[#F4F4F2]" data-testid="english-preview">
                  <button
                    onClick={() => setShowPreview((v) => !v)}
                    data-testid="toggle-preview"
                    className="w-full flex items-center justify-between p-3 hover:bg-black/[0.03] transition-colors duration-150"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] tracking-[0.2em] uppercase bg-[#D4FF00] text-[#0A0A0A] px-1.5 py-0.5" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                        EN
                      </span>
                      <span className="text-[10px] tracking-[0.25em] uppercase text-[#525252]" style={{ fontFamily: "'Chivo', sans-serif", fontWeight: 700 }}>
                        English preview (won't be sent)
                      </span>
                    </div>
                    <span className="text-xs text-[#525252]">{showPreview ? "hide" : "show"}</span>
                  </button>
                  {showPreview && (
                    <div className="px-4 pb-4 text-sm whitespace-pre-wrap text-[#0A0A0A]/85 leading-relaxed" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                      {preview}
                    </div>
                  )}
                </div>
              )}
              {email && !currentTarget?.email && (
                <div className="border-t border-black/10 p-3 text-xs text-[#525252] flex items-start gap-2" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                  <span className="text-yellow-700">💡</span>
                  <span>
                    No email on file. Click <b>OPEN IN MAIL APP</b> to launch your mail composer with subject + body pre-filled — then paste in their address from their website's contact page.
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
